import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { usePayment } from './PaymentContext';

const ShippingContext = createContext();

export const useShipping = () => {
  const context = useContext(ShippingContext);
  if (!context) {
    throw new Error('useShipping must be used within a ShippingProvider');
  }
  return context;
};

export const ShippingProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const { user } = useAuth();
  const { processPayment } = usePayment();

  // Available carriers with their configurations
  const availableCarriers = [
    {
      id: 'fedex',
      name: 'FedEx',
      logo: '📦',
      services: [
        { id: 'fedex_ground', name: 'Ground', deliveryTime: '1-5 días hábiles' },
        { id: 'fedex_express', name: 'Express', deliveryTime: '1-3 días hábiles' },
        { id: 'fedex_overnight', name: 'Overnight', deliveryTime: '1 día hábil' }
      ],
      baseRate: 1.0,
      weightMultiplier: 15.5,
      distanceMultiplier: 0.8
    },
    {
      id: 'dhl',
      name: 'DHL',
      logo: '🚚',
      services: [
        { id: 'dhl_express', name: 'Express', deliveryTime: '1-3 días hábiles' },
        { id: 'dhl_economy', name: 'Economy', deliveryTime: '3-5 días hábiles' },
        { id: 'dhl_overnight', name: 'Overnight', deliveryTime: '1 día hábil' }
      ],
      baseRate: 1.2,
      weightMultiplier: 16.0,
      distanceMultiplier: 0.9
    },
    {
      id: 'ups',
      name: 'UPS',
      logo: '📫',
      services: [
        { id: 'ups_ground', name: 'Ground', deliveryTime: '1-5 días hábiles' },
        { id: 'ups_express', name: 'Express', deliveryTime: '1-3 días hábiles' },
        { id: 'ups_next_day', name: 'Next Day', deliveryTime: '1 día hábil' }
      ],
      baseRate: 0.95,
      weightMultiplier: 14.8,
      distanceMultiplier: 0.85
    },
    {
      id: 'estafeta',
      name: 'Estafeta',
      logo: '🇲🇽',
      services: [
        { id: 'estafeta_standard', name: 'Standard', deliveryTime: '2-4 días hábiles' },
        { id: 'estafeta_next_day', name: 'Día Siguiente', deliveryTime: '1 día hábil' },
        { id: 'estafeta_express', name: 'Express', deliveryTime: '1-2 días hábiles' }
      ],
      baseRate: 0.8,
      weightMultiplier: 12.0,
      distanceMultiplier: 0.6
    },
    {
      id: 'redpack',
      name: 'Redpack',
      logo: '🔴',
      services: [
        { id: 'redpack_standard', name: 'Standard', deliveryTime: '2-5 días hábiles' },
        { id: 'redpack_express', name: 'Express', deliveryTime: '1-3 días hábiles' },
        { id: 'redpack_same_day', name: 'Same Day', deliveryTime: 'Mismo día' }
      ],
      baseRate: 0.85,
      weightMultiplier: 13.2,
      distanceMultiplier: 0.7
    },
    {
      id: 'paquete_express',
      name: 'Paquete Express',
      logo: '📮',
      services: [
        { id: 'paquete_standard', name: 'Standard', deliveryTime: '3-6 días hábiles' },
        { id: 'paquete_express', name: 'Express', deliveryTime: '1-3 días hábiles' },
        { id: 'paquete_overnight', name: 'Overnight', deliveryTime: '1 día hábil' }
      ],
      baseRate: 0.75,
      weightMultiplier: 11.5,
      distanceMultiplier: 0.65
    }
  ];

  // Initialize carriers on mount
  useEffect(() => {
    setCarriers(availableCarriers);
  }, []);

  // Load user's shipments when user changes
  useEffect(() => {
    if (user) {
      loadUserShipments();
    } else {
      setShipments([]);
    }
  }, [user]);

  const loadUserShipments = async () => {
    try {
      setLoading(true);
      setError(null);

      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 800));

      // Mock user shipments
      const mockShipments = [
        {
          id: 'SHP-001',
          trackingNumber: 'TRK-123456789',
          carrier: 'fedex',
          service: 'fedex_express',
          origin: 'Ciudad de México, CDMX',
          destination: 'Guadalajara, JAL',
          weight: 2.5,
          dimensions: { length: 30, width: 25, height: 15 },
          cost: 285.50,
          status: 'in_transit',
          createdAt: new Date('2025-01-15').toISOString(),
          estimatedDelivery: new Date('2025-01-17').toISOString()
        },
        {
          id: 'SHP-002',
          trackingNumber: 'TRK-987654321',
          carrier: 'dhl',
          service: 'dhl_express',
          origin: 'Monterrey, NL',
          destination: 'Puebla, PUE',
          weight: 1.2,
          dimensions: { length: 20, width: 20, height: 10 },
          cost: 198.75,
          status: 'delivered',
          createdAt: new Date('2025-01-12').toISOString(),
          estimatedDelivery: new Date('2025-01-14').toISOString()
        }
      ];

      setShipments(mockShipments);
    } catch (error) {
      setError(error.message || 'Error loading shipments');
    } finally {
      setLoading(false);
    }
  };

  const validateAddress = async (address) => {
    try {
      // TODO: Integrate with actual address validation API
      await new Promise(resolve => setTimeout(resolve, 500));

      // Mock address validation
      const validatedAddress = {
        ...address,
        isValid: true,
        normalizedAddress: address.address,
        city: address.city,
        state: address.state,
        zipCode: address.zipCode,
        country: address.country || 'México'
      };

      // Simulate some validation errors
      if (address.zipCode?.length !== 5) {
        validatedAddress.isValid = false;
        validatedAddress.error = 'Código postal inválido';
      }

      return { success: true, validatedAddress };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const calculateDistance = (origin, destination) => {
    // Mock distance calculation based on cities
    const distances = {
      'Ciudad de México-Guadalajara': 550,
      'Ciudad de México-Monterrey': 920,
      'Ciudad de México-Puebla': 130,
      'Guadalajara-Monterrey': 780,
      'Guadalajara-Puebla': 680,
      'Monterrey-Puebla': 1050
    };

    const key = `${origin}-${destination}`;
    const reverseKey = `${destination}-${origin}`;
    
    return distances[key] || distances[reverseKey] || 500; // Default distance
  };

  const calculateQuote = (shippingData) => {
    const { origin, destination, weight, dimensions, insurance } = shippingData;
    
    // Calculate volume weight
    const volumeWeight = (dimensions.length * dimensions.width * dimensions.height) / 5000;
    const billableWeight = Math.max(weight, volumeWeight);
    
    // Calculate distance
    const distance = calculateDistance(origin.city, destination.city);
    
    // Generate quotes for all carriers
    const quotes = carriers.map(carrier => {
      return carrier.services.map(service => {
        // Base calculation
        let basePrice = carrier.baseRate * 100;
        let weightPrice = billableWeight * carrier.weightMultiplier;
        let distancePrice = distance * carrier.distanceMultiplier;
        
        // Service multiplier
        let serviceMultiplier = 1;
        if (service.id.includes('express')) serviceMultiplier = 1.3;
        if (service.id.includes('overnight') || service.id.includes('next_day')) serviceMultiplier = 1.8;
        if (service.id.includes('same_day')) serviceMultiplier = 2.5;
        
        let totalPrice = (basePrice + weightPrice + distancePrice) * serviceMultiplier;
        
        // Add insurance if requested
        if (insurance) {
          totalPrice += 50; // Fixed insurance cost
        }
        
        // Add random variation to simulate real pricing
        const variation = 0.9 + Math.random() * 0.2; // ±10% variation
        totalPrice = Math.round(totalPrice * variation * 100) / 100;
        
        return {
          id: `${carrier.id}_${service.id}`,
          carrier: carrier.id,
          carrierName: carrier.name,
          carrierLogo: carrier.logo,
          service: service.id,
          serviceName: service.name,
          price: totalPrice,
          estimatedDelivery: service.deliveryTime,
          includesInsurance: insurance,
          features: [
            'Rastreo en tiempo real',
            'Recolección incluida',
            'Seguro básico incluido',
            ...(insurance ? ['Seguro adicional'] : [])
          ]
        };
      });
    }).flat();
    
    // Sort by price
    return quotes.sort((a, b) => a.price - b.price);
  };

  const getQuotes = async (shippingData) => {
    try {
      setLoading(true);
      setError(null);

      // Validate addresses
      const originValidation = await validateAddress(shippingData.origin);
      const destinationValidation = await validateAddress(shippingData.destination);

      if (!originValidation.success || !destinationValidation.success) {
        throw new Error('Error validating addresses');
      }

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Calculate quotes
      const calculatedQuotes = calculateQuote(shippingData);
      
      setQuotes(calculatedQuotes);
      return { success: true, quotes: calculatedQuotes };
    } catch (error) {
      setError(error.message || 'Error getting quotes');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const createShipment = async (quoteId, shippingData) => {
    try {
      setLoading(true);
      setError(null);

      // Find selected quote
      const selectedQuote = quotes.find(q => q.id === quoteId);
      if (!selectedQuote) {
        throw new Error('Quote not found');
      }

      // Process payment
      const paymentResult = await processPayment(
        selectedQuote.price,
        `Envío ${selectedQuote.carrierName} - ${selectedQuote.serviceName}`
      );

      if (!paymentResult.success) {
        throw new Error(paymentResult.error);
      }

      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Create shipment
      const newShipment = {
        id: `SHP-${Date.now()}`,
        trackingNumber: `TRK-${Date.now()}`,
        carrier: selectedQuote.carrier,
        service: selectedQuote.service,
        origin: `${shippingData.origin.city}, ${shippingData.origin.state}`,
        destination: `${shippingData.destination.city}, ${shippingData.destination.state}`,
        weight: shippingData.weight,
        dimensions: shippingData.dimensions,
        cost: selectedQuote.price,
        status: 'processing',
        createdAt: new Date().toISOString(),
        estimatedDelivery: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString()
      };

      setShipments(prev => [newShipment, ...prev]);
      return { success: true, shipment: newShipment };
    } catch (error) {
      setError(error.message || 'Error creating shipment');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const trackShipment = async (trackingNumber) => {
    try {
      setLoading(true);
      setError(null);

      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Mock tracking data
      const trackingData = {
        trackingNumber,
        status: 'in_transit',
        lastUpdate: new Date().toISOString(),
        events: [
          {
            date: new Date('2025-01-15').toISOString(),
            status: 'pickup',
            description: 'Paquete recolectado',
            location: 'Ciudad de México, CDMX'
          },
          {
            date: new Date('2025-01-16').toISOString(),
            status: 'in_transit',
            description: 'En tránsito',
            location: 'Centro de distribución'
          },
          {
            date: new Date('2025-01-16').toISOString(),
            status: 'out_for_delivery',
            description: 'En reparto',
            location: 'Guadalajara, JAL'
          }
        ]
      };

      return { success: true, trackingData };
    } catch (error) {
      setError(error.message || 'Error tracking shipment');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const clearQuotes = () => {
    setQuotes([]);
  };

  const value = {
    loading,
    error,
    quotes,
    shipments,
    carriers,
    getQuotes,
    createShipment,
    trackShipment,
    validateAddress,
    clearError,
    clearQuotes,
    refreshShipments: loadUserShipments
  };

  return (
    <ShippingContext.Provider value={value}>
      {children}
    </ShippingContext.Provider>
  );
};

export default ShippingContext;